import { useMutation } from "@apollo/client";
import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { ColumnDef } from "@tanstack/react-table";
import Checkbox from "@/components/Checkbox";
import EmptyState from "@/components/EmptyState";
import Tag from "@/components/Tag";
import Table, { TableControls, TableFooter, buildActionColumn, type TableAction } from "@/components/Table";
import { renderEmptyCellState } from "@/components/Table/utils";
import type { useBulkSelection } from "@/hooks/useBulkSelection";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { toFormat } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";
import type { MemberElectionRunNode } from "../types";
import {
  ELECTION_RUN_STATUS_LABELS,
  ELECTION_RUN_STATUS_TAG_TYPE,
  ELECTION_RUN_TRIGGER_LABELS
} from "../_utils/electionRunLabels";
import { GENERATE_ELECTION_MAGAZINE_LIST_MUTATION } from "../mutations";
import DownloadPastPresidentsRosterDialog, {
  formatPastPresidentsElectionSummary
} from "./DownloadPastPresidentsRosterDialog";

/** Local selection column so Past Elections can set accessible labels without changing the shared Table helper. */
function buildSelectionColumn(): ColumnDef<MemberElectionRunNode> {
  return {
    id: "select",
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    meta: {
      className: "pr-0",
      headerClassName: "pr-0"
    },
    header: ({ table }) => (
      <Checkbox
        name="select-all"
        checked={table.getIsAllRowsSelected()}
        indeterminate={table.getIsSomeRowsSelected()}
        onChange={(checked) => table.toggleAllRowsSelected(checked)}
        aria-label="Select all election runs on this page"
      />
    ),
    cell: ({ row }) => (
      <div onClick={(e) => e.stopPropagation()}>
        <Checkbox
          name={`select-row-${row.id}`}
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={(checked) => row.toggleSelected(checked)}
          aria-label={`Select election run started ${toFormat(row.original.startedAt, "MMM dd, yyyy h:mm a") ?? row.id}`}
        />
      </div>
    )
  };
}

function buildColumns(
  onDownloadMagazineList: (run: MemberElectionRunNode) => void,
  onDownloadPastPresidentsRoster: (run: MemberElectionRunNode) => void,
  downloadingRunId: string | null,
  isBulkDownloading: boolean,
  pastPresidentsDialogOpen: boolean,
  setMenu: ReturnType<typeof useTableRowMenu>["setMenu"]
): ColumnDef<MemberElectionRunNode, unknown>[] {
  return [
    {
      id: "startedAt",
      header: "Started",
      accessorFn: (row) => row.startedAt,
      cell: ({ row }) => toFormat(row.original.startedAt, "MMM dd, yyyy h:mm a") ?? "--",
      size: 200,
      enableSorting: true
    },
    {
      id: "completedAt",
      header: "Completed",
      accessorFn: (row) => row.completedAt,
      cell: ({ row }) =>
        row.original.completedAt ? (toFormat(row.original.completedAt, "MMM dd, yyyy h:mm a") ?? "--") : "--",
      size: 200
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => row.status,
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Tag tagType={ELECTION_RUN_STATUS_TAG_TYPE[status]} tagStyle="strong" size="small">
            {ELECTION_RUN_STATUS_LABELS[status]}
          </Tag>
        );
      },
      size: 160
    },
    {
      id: "triggerType",
      header: "Trigger",
      accessorFn: (row) => row.triggerType,
      cell: ({ row }) => ELECTION_RUN_TRIGGER_LABELS[row.original.triggerType],
      size: 120
    },
    {
      id: "totalCount",
      header: "Applications",
      accessorFn: (row) => row.totalCount,
      cell: ({ getValue }) => String(getValue<number>()),
      size: 120
    },
    {
      id: "initiatedBy",
      header: "Initiated By",
      accessorFn: (row) => row.initiatedBy?.name,
      cell: ({ getValue }) => renderEmptyCellState(getValue<string | undefined>()),
      size: 180
    },
    buildActionColumn<MemberElectionRunNode>({
      getActions: (row) => [
        {
          text: "Download PGA Magazine list",
          section: "Actions",
          iconName: "download",
          disabled: downloadingRunId === row.id || isBulkDownloading,
          onClick: () => onDownloadMagazineList(row)
        },
        {
          text: "Download Past Presidents roster",
          section: "Actions",
          iconName: "download",
          disabled: pastPresidentsDialogOpen || row.status !== "COMPLETED" || row.completedAt == null,
          onClick: () => onDownloadPastPresidentsRoster(row)
        }
      ],
      actionId,
      setMenu
    })
  ];
}

export type PastMemberElectionRunsTableProps = {
  loading: boolean;
  runs: MemberElectionRunNode[];
  selectedRunById: Record<string, MemberElectionRunNode>;
  showLoadError: boolean;
  bulkSelection: ReturnType<typeof useBulkSelection>;
  createHeaderPagination: ReactNode;
  createFooterPagination: ReactNode;
};

export default function PastMemberElectionRunsTable({
  loading,
  runs,
  selectedRunById,
  showLoadError,
  bulkSelection,
  createHeaderPagination,
  createFooterPagination
}: PastMemberElectionRunsTableProps) {
  const {
    rowSelection,
    setRowSelection,
    bulkSelectionProps,
    isSelectedOnly,
    deselectAll,
    rowSelectionCount,
    orderedSelectedIds
  } = bulkSelection;

  const { Menu, setMenu } = useTableRowMenu();
  const downloadingRunIdRef = useRef<string | null>(null);
  const isBulkDownloadingRef = useRef(false);
  const [downloadingRunId, setDownloadingRunId] = useState<string | null>(null);
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [pastPresidentsDialog, setPastPresidentsDialog] = useState<
    | null
    | { mode: "single"; run: MemberElectionRunNode }
    | { mode: "bulk"; runIds: string[]; runs: MemberElectionRunNode[] }
  >(null);
  const [generateElectionMagazineList] = useMutation(GENERATE_ELECTION_MAGAZINE_LIST_MUTATION);

  const exportableRunIds = useMemo(() => {
    const lookup = new Map(runs.map((run) => [run.id, run]));
    const selectedIds = orderedSelectedIds.filter((id) => rowSelection[id]);
    const extraSelectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id] && !selectedIds.includes(id));
    return [...selectedIds, ...extraSelectedIds].filter((id) => {
      const run = selectedRunById[id] ?? lookup.get(id);
      return run != null && run.status === "COMPLETED" && run.completedAt != null;
    });
  }, [orderedSelectedIds, rowSelection, runs, selectedRunById]);

  const handleDownloadMagazineList = useCallback(
    async (run: MemberElectionRunNode) => {
      if (downloadingRunIdRef.current || isBulkDownloadingRef.current) return;

      downloadingRunIdRef.current = run.id;
      setDownloadingRunId(run.id);
      try {
        const { data } = await generateElectionMagazineList({
          variables: {
            input: {
              electionRunId: run.id
            }
          }
        });
        const result = data?.generateElectionMagazineList;

        if (!result?.success || !result.url) {
          showErrorSnackbar("PGA Magazine list not generated", {
            subtitle: result?.message || "Please try again later."
          });
          return;
        }

        showSnackbar(result.message || "Export generated");
        window.location.assign(result.url);
      } catch {
        showErrorSnackbar("PGA Magazine list not generated", { subtitle: "Please try again later." });
      } finally {
        downloadingRunIdRef.current = null;
        setDownloadingRunId(null);
      }
    },
    [generateElectionMagazineList]
  );

  const handleDownloadPastPresidentsRoster = useCallback((run: MemberElectionRunNode) => {
    setPastPresidentsDialog({ mode: "single", run });
  }, []);

  const handleBulkDownloadPastPresidentsRosters = useCallback(() => {
    if (exportableRunIds.length < 2) return;

    const lookup = new Map(runs.map((run) => [run.id, run]));
    const selectedRuns = exportableRunIds
      .map((id) => selectedRunById[id] ?? lookup.get(id))
      .filter((run): run is MemberElectionRunNode => run != null);

    setPastPresidentsDialog({
      mode: "bulk",
      runIds: exportableRunIds,
      runs: selectedRuns
    });
  }, [exportableRunIds, runs, selectedRunById]);

  const handleBulkDownloadMagazineLists = useCallback(async () => {
    if (isBulkDownloadingRef.current || downloadingRunIdRef.current || exportableRunIds.length < 2) return;

    isBulkDownloadingRef.current = true;
    setIsBulkDownloading(true);
    try {
      const { data } = await generateElectionMagazineList({
        variables: {
          input: {
            electionRunIds: exportableRunIds
          }
        }
      });
      const result = data?.generateElectionMagazineList;

      if (!result?.success || !result.url) {
        showErrorSnackbar("PGA Magazine lists not generated", {
          subtitle: result?.message || "Please try again later."
        });
        return;
      }

      showSnackbar(result.message || "Export generated");
      window.location.assign(result.url);
    } catch {
      showErrorSnackbar("PGA Magazine lists not generated", { subtitle: "Please try again later." });
    } finally {
      isBulkDownloadingRef.current = false;
      setIsBulkDownloading(false);
    }
  }, [exportableRunIds, generateElectionMagazineList]);

  const bulkActions = useMemo<TableAction[]>(
    () =>
      exportableRunIds.length >= 2
        ? [
            {
              label: "Download PGA Magazine lists",
              icon: "download",
              variant: "outlined",
              onClick: handleBulkDownloadMagazineLists,
              disabled: isBulkDownloading,
              testId: "download-magazine-lists-bulk-button"
            },
            {
              label: "Download Past Presidents rosters",
              icon: "download",
              variant: "outlined",
              onClick: handleBulkDownloadPastPresidentsRosters,
              disabled: pastPresidentsDialog != null,
              testId: "download-past-presidents-rosters-bulk-button"
            }
          ]
        : [],
    [
      exportableRunIds.length,
      handleBulkDownloadMagazineLists,
      handleBulkDownloadPastPresidentsRosters,
      isBulkDownloading,
      pastPresidentsDialog
    ]
  );

  const columns = useMemo(
    () => [
      buildSelectionColumn(),
      ...buildColumns(
        handleDownloadMagazineList,
        handleDownloadPastPresidentsRoster,
        downloadingRunId,
        isBulkDownloading,
        pastPresidentsDialog != null,
        setMenu
      )
    ],
    [
      downloadingRunId,
      handleDownloadMagazineList,
      handleDownloadPastPresidentsRoster,
      isBulkDownloading,
      pastPresidentsDialog,
      setMenu
    ]
  );

  const displayRuns = useMemo(() => {
    if (!isSelectedOnly) {
      return runs;
    }
    return orderedSelectedIds
      .map((id) => selectedRunById[id] ?? runs.find((run) => run.id === id))
      .filter((run): run is MemberElectionRunNode => run != null);
  }, [isSelectedOnly, orderedSelectedIds, runs, selectedRunById]);

  return (
    <>
      <Table
        loading={loading}
        data={displayRuns}
        columns={columns}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={setRowSelection}
        hasActiveFilters={isSelectedOnly && !showLoadError}
        data-testid="past-member-election-runs-table"
        renderControls={(table) => (
          <TableControls
            pagination={!isSelectedOnly ? createHeaderPagination : undefined}
            bulkActions={bulkActions}
            {...bulkSelectionProps}
            onDeselectAll={deselectAll}
            totalRowCount={rowSelectionCount}
            onToggleAllRows={(checked) => table.toggleAllRowsSelected(checked)}
            allRowsSelected={table.getIsAllRowsSelected()}
            someRowsSelected={table.getIsSomeRowsSelected()}
          />
        )}
        renderFooter={() => (
          <TableFooter pagination={!isSelectedOnly ? createFooterPagination : undefined} {...bulkSelectionProps} />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            iconName="history"
            title={showLoadError ? "Unable to load past elections" : "No past elections"}
            caption={
              showLoadError
                ? "Try refreshing the page."
                : "Finalize elections from the Pending Electees tab to create election run history."
            }
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState
            iconName="history"
            title="No Selected Rows on This Page"
            caption="Selected election runs on other pages are not shown here. Turn off Show Selected Only to return to the full list."
          />
        )}
      />
      <Menu />
      <DownloadPastPresidentsRosterDialog
        open={pastPresidentsDialog != null}
        electionRunId={pastPresidentsDialog?.mode === "single" ? pastPresidentsDialog.run.id : undefined}
        electionRunIds={pastPresidentsDialog?.mode === "bulk" ? pastPresidentsDialog.runIds : undefined}
        electionSummary={
          pastPresidentsDialog?.mode === "single"
            ? formatPastPresidentsElectionSummary([pastPresidentsDialog.run])
            : pastPresidentsDialog?.mode === "bulk"
              ? formatPastPresidentsElectionSummary(pastPresidentsDialog.runs)
              : undefined
        }
        onClose={() => setPastPresidentsDialog(null)}
      />
    </>
  );
}
